import { useCallback } from 'react';
import { IRelation } from './RelationForm';
import { useWatch } from 'antd/es/form/Form';
import { ADMIN_ROUTES } from 'constants/routes';
import { useNavigate, useParams } from 'react-router-dom';
import { usePurchaseRequestBrandSelectorListQuery, useSupplierItemSelectorListQuery } from 'api';
import { SUPPLIER_OFFER_SOURCE } from 'models/SupplierOffer';

export const useRelation = (props: IRelation) => {
  const { form, relationList, onSave, onRemove, onEdit } = props;
  const { id } = useParams<{ id: string }>() as { id: string };
  const navigate = useNavigate();
  const relationBrand = useWatch('relationBrand', form);
  const relationSupplierOffer = useWatch('relationSupplierOffer', form);

  const { data: purchaseRequestBrand = [] } = usePurchaseRequestBrandSelectorListQuery({ dealId: id });
  const { data: supplierOffers = [] } = useSupplierItemSelectorListQuery(
    { purchaseRequestId: relationBrand },
    { skip: !relationBrand }
  );

  const onRelationCancel = useCallback(() => {
    form.resetFields(['relationBrand']);
    form.resetFields(['relationSupplierOffer']);
  }, [form]);

  const onRelationSave = useCallback(async () => {
    try {
      await form.validateFields(['relationBrand', 'relationSupplierOffer']);
      const { relationBrand, relationSupplierOffer } = form.getFieldsValue(['relationBrand', 'relationSupplierOffer']);
      onSave([
        ...relationList,
        {
          relationBrand,
          relationSupplierOffer,
        },
      ]);
      form.resetFields(['relationBrand', 'relationSupplierOffer']);
    } catch {
      //handle
    }
  }, [form, relationList, onSave]);

  const onEditRelationSave = useCallback(
    async (relationBrand: string, relationSupplierOffer: string) => {
      try {
        await form.validateFields(['editRelationBrand', 'editRelationSupplierOffer']);
        const { editRelationBrand, editRelationSupplierOffer } = form.getFieldsValue([
          'editRelationBrand',
          'editRelationSupplierOffer',
        ]);
        onSave(
          relationList.map((relation) => {
            if (relation.relationSupplierOffer === relationSupplierOffer && relation.relationBrand === relationBrand) {
              return {
                ...relation,
                relationBrand: editRelationBrand,
                relationSupplierOffer: editRelationSupplierOffer,
              };
            } else {
              return relation;
            }
          })
        );
        form.resetFields(['editRelationBrand', 'editRelationSupplierOffer']);
      } catch {
        // handle catch
      }
    },
    [form, onSave, relationList]
  );

  const onRelationEdit = useCallback(
    (relationBrand: string, relationSupplierOffer: string) => {
      onEdit && onEdit(relationBrand, relationSupplierOffer);
      const relation = relationList.find(
        (relation) =>
          relation.relationSupplierOffer === relationSupplierOffer && relation.relationBrand === relationBrand
      );
      if (relation) {
        form.setFieldsValue({
          editRelationBrand: relation.relationBrand,
          editRelationSupplierOffer: relation.relationSupplierOffer,
        });
      }
    },
    [relationList, form, onEdit]
  );

  const onRelationRemove = useCallback(
    (relationBrand: string, relationSupplierOffer: string) => {
      onRemove &&
        onRemove(
          relationList.filter(
            (relation: any) =>
              relation.relationSupplierOffer !== relationSupplierOffer || relation.relationBrand !== relationBrand
          )
        );
    },
    [relationList, onRemove]
  );

  const onRedirectToSupplierOffer = useCallback(
    (purchaseRequestId: string, supplierOfferId: string) => {
      navigate(
        `${ADMIN_ROUTES.purchaseRequest.view}/${purchaseRequestId}/${ADMIN_ROUTES.purchaseRequest.supplierOfferUpdate}`
          .replace(':source', SUPPLIER_OFFER_SOURCE.PR_DETAILS)
          .replace(':supplierOfferId', supplierOfferId)
      );
    },
    [navigate]
  );
  const onRedirectToPurchaseRequestSupplierOffers = useCallback(
    (purchaseRequestId: string) => {
      navigate(
        `${ADMIN_ROUTES.purchaseRequest.view}/${purchaseRequestId}/${ADMIN_ROUTES.purchaseRequest.supplierOffers}`
      );
    },
    [navigate]
  );

  return {
    onRelationCancel,
    onRelationSave,
    onEditRelationSave,
    onRelationRemove,
    onRelationEdit,
    isChanged: !!relationBrand || !!relationSupplierOffer,
    onRedirectToSupplierOffer,
    purchaseRequestBrand,
    supplierOffers,
    onRedirectToPurchaseRequestSupplierOffers,
  };
};
